import { addYears, isValid } from 'date-fns';
import { z } from 'zod';

import type { FilesValue } from '@/components/forms/files';
import { toDate, type DateInput } from '@/lib/dates';

/** Valeurs du formulaire « Je modifie mes coordonnées bancaires ». */
export interface ChangeRibValues {
    tiers?: number | string;
    iban?: string;
    domiciliation?: string;
    bic?: string;
    adresse: string;
    cp: string;
    ville: string;
    titulaire: string;
    /** Tiers payeur : `P` (personne physique) ou `M` (personne morale). */
    type: string | null;
    firstname: string;
    lastname: string;
    birthname: string;
    birthdate: Date | string | null;
    birthcity: string;
    birthcountry: string;
    relationship: string;
    third_payeur_reason: string;
    /** Renseigné quand le titulaire diffère du souscripteur (tiers payeur). */
    contract?: number | string;
    pays?: string;
    files: FilesValue;
}

const isDateInput = (value: unknown): value is DateInput =>
    value === null || value instanceof Date || typeof value === 'string' || typeof value === 'number';

/**
 * Contrôle du changement de coordonnées bancaires ; `changeTitulaire` ajoute l'adresse du
 * nouveau titulaire (ancien contexte du résolveur).
 */
export const createChangeRibSchema = (changeTitulaire: boolean) =>
    z.looseObject({}).superRefine((values, ctx) => {
        const v = values as Record<string, unknown>;
        const errors: Record<string, string> = {};

        let requiredFields = ['iban', 'domiciliation', 'bic'];

        if (changeTitulaire) {
            requiredFields = requiredFields.concat(['titulaire', 'adresse', 'cp', 'ville']);
        }

        // tiers payeur
        if (v['contract']) {
            requiredFields.push('type');
        }

        if (v['type']) {
            if (v['type'] === 'P') {
                requiredFields = requiredFields.concat([
                    'firstname',
                    'lastname',
                    'birthname',
                    'birthdate',
                    'birthcountry',
                    'birthcity',
                    'relationship',
                ]);

                if (v['relationship'] && v['relationship'] === 'A') {
                    requiredFields.push('third_payeur_reason');
                }
                if (v['birthdate'] !== undefined) {
                    const birthdate = toDate(isDateInput(v['birthdate']) ? v['birthdate'] : null);
                    if (!isValid(birthdate) || addYears(birthdate, 14).getTime() >= Date.now()) {
                        errors['birthdate'] = 'Le tiers payeur doit avoir au moins 14 ans.';
                    }
                }
            } else {
                requiredFields = requiredFields.concat(['lastname']);
            }
        }

        for (const field of requiredFields) {
            if (!v[field]) errors[field] = 'Le champ est obligatoire';
        }

        for (const [path, message] of Object.entries(errors)) ctx.addIssue({ code: 'custom', path: [path], message });
    });
